import { ConfigService } from '@axo/config/ConfigService';
import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { TextFieldUI } from '@axo/form/feature/form-input/TextField/TextFieldUI';
import { EmploymentStatus } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useFormContext } from 'react-hook-form';
import { useBroker } from '../../../../../../hooks/useBroker';
import { gridColumns } from '../../../config/layouts/grid';
import { EmploymentStatusField } from '../../../fields/EmploymentStatusField';
import { GrossIncomeMonthlyField } from '../../../fields/GrossIncomeMonthlyField';
import { EmploymentFormGroupData } from '../../../FormData.se.types';
import styles from '../../../loan.module.scss';
import { employerFieldOptions } from '../../shared-config';
import {
  employmentStatusSinceRulesOptions,
  employmentStatusUntilRulesOptions,
} from './config';

export const EmploymentFormGroup = ({
  applicant = '',
}: {
  applicant?: '' | 'CoApplicant';
}) => {
  const { t } = useTranslation();

  const broker = useBroker();

  const { alwaysAskEmploymentStart } = ConfigService.get(
    'product-unsecured-loan'
  ).fields[broker];

  const { watch } = useFormContext();

  const currentYear = new Date().getFullYear();

  const employment = watch(`LoanApplication/${applicant}EmploymentStatus`);

  const employmentTypesWithStartDate: EmploymentStatus[] = [
    EmploymentStatus.Permanent,
    EmploymentStatus.Temporary,
    EmploymentStatus.Entrepreneur,
    EmploymentStatus.Hourly,
    EmploymentStatus.Project,
    EmploymentStatus.Probationary,
  ];

  const employmentTypesWithEndDate: EmploymentStatus[] = [
    EmploymentStatus.Temporary,
    EmploymentStatus.Project,
  ];

  const requireEmployer = employmentTypesWithStartDate.includes(employment);

  const requireEmploymentStart = alwaysAskEmploymentStart || requireEmployer;

  const requireEmploymentEnd = employmentTypesWithEndDate.includes(employment);

  return (
    <Grid columns={gridColumns}>
      <EmploymentStatusField applicant={applicant} />

      <GrossIncomeMonthlyField applicant={applicant} />

      {requireEmployer && (
        <TextFieldUI<EmploymentFormGroupData>
          name={`LoanApplication/${applicant}Employer`}
          label={t('Employer.label')}
          data-testid="employer-input"
          {...employerFieldOptions}
        />
      )}

      {requireEmploymentStart && (
        <>
          {/* TODO: Fix styles DateFieldUI */}
          <div className={styles.dateFieldUI}>
            <DateFieldUI<EmploymentFormGroupData>
              name={`LoanApplication/${applicant}EmploymentStatusSince`}
              label={t('EmploymentStatusSince.label')}
              labelYearOnly={t('EmploymentStatusSince.year.label')}
              minYear={currentYear - 10}
              maxYear={currentYear}
              disableMonthsUntilYear={currentYear - 3}
              minYearSuffix={t('EmploymentStatusSince.year.suffix')}
              data-testid="employment-status-since-dropdown"
              i18n="sv"
              disableDatesInFuture
              latestYearAtTop
              rules={employmentStatusSinceRulesOptions}
            />
          </div>
        </>
      )}

      {/* TODO: Fix styles DateFieldUI */}
      {requireEmploymentEnd && (
        <div className={styles.dateFieldUI}>
          <DateFieldUI<EmploymentFormGroupData>
            name={`LoanApplication/${applicant}EmploymentStatusUntil`}
            label={t('EmploymentStatusUntil.label')}
            labelYearOnly={t('EmploymentStatusUntil.year.label')}
            data-testid="employment-status-until-dropdown"
            i18n="sv"
            minYear={currentYear}
            maxYear={currentYear + 3}
            disableDatesInPast
            latestYearAtTop
            rules={employmentStatusUntilRulesOptions}
          />
        </div>
      )}
    </Grid>
  );
};
